import { useCallback, useEffect, useState } from "react";
import { FiEye, FiSearch } from "react-icons/fi";
import { paymentService } from "../../../services/payment.service";
import DataTableToolbar from "../../../components/common/DataTableToolbar";
import PaginationBar from "../../../components/common/PaginationBar";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readPagination } from "../../../utils/listPagination";
import { formatRepoRole } from "../../../constants/repoRoles";
import { getApiErrorMessage, saveExcelBlob } from "../../../utils/downloadExcel";
import { printTablePage } from "../../../utils/printTable";
import "../../../styles/users.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatAmount(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function paidByName(row) {
  return row?.paidBy?.name || row?.paidBy?.email || "—";
}

function companyName(row) {
  return row?.company?.companyName || "—";
}

function unwrapPayments(res) {
  const payload = res?.data?.data || res?.data || {};
  const list = payload.payments || payload.items || [];
  return Array.isArray(list) ? list : [];
}

function unwrapPayment(res) {
  const payload = res?.data?.data || res?.data || {};
  return payload.payment || null;
}

function unwrapTotalAmount(res) {
  const payload = res?.data?.data || res?.data || {};
  return Number(payload.totalAmount || 0);
}

function unwrapTodayAmount(res) {
  const payload = res?.data?.data || res?.data || {};
  return Number(payload.todayAmount || 0);
}

function DetailRow({ label, children }) {
  return (
    <div className="payment-detail-row">
      <dt>{label}</dt>
      <dd>{children || "—"}</dd>
    </div>
  );
}

function PaymentDetailModal({ payment, loading, error, onClose }) {
  if (!payment && !loading && !error) return null;

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-box payment-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="payment-detail-title">Payment details</h3>
        <p className="modal-subtitle">Successful payment record</p>
        {loading ? <p className="muted">Loading…</p> : null}
        {error ? <p className="error-text">{error}</p> : null}
        {!loading && payment ? (
          <dl className="payment-detail-grid">
            <DetailRow label="Payment date">{formatDateTime(payment.paidAt || payment.createdAt)}</DetailRow>
            <DetailRow label="Paid by">{paidByName(payment)}</DetailRow>
            <DetailRow label="Admin role">{formatRepoRole(payment.paidBy?.role)}</DetailRow>
            <DetailRow label="Admin email">{payment.paidBy?.email || "—"}</DetailRow>
            <DetailRow label="Admin phone">{payment.paidBy?.phone || "—"}</DetailRow>
            <DetailRow label="Company">{companyName(payment)}</DetailRow>
            <DetailRow label="Company code">{payment.company?.companyCode || "—"}</DetailRow>
            <DetailRow label="Type / plan">{payment.planLabel || "—"}</DetailRow>
            <DetailRow label="Amount">
              {formatAmount(payment.amount)} {payment.currency || "INR"}
            </DetailRow>
            <DetailRow label="Status">{payment.status || "—"}</DetailRow>
            <DetailRow label="Source">{payment.paymentSource || "RAZORPAY"}</DetailRow>
            <DetailRow label="Razorpay payment ID">{payment.razorpayPaymentId || "—"}</DetailRow>
            <DetailRow label="Razorpay order ID">{payment.razorpayOrderId || "—"}</DetailRow>
          </dl>
        ) : null}
        <div className="modal-actions">
          <button type="button" className="secondary-page-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SsdiPayments() {
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [totalAmount, setTotalAmount] = useState(0);
  const [todayAmount, setTodayAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = { page, limit: PAGE_LIMIT };
      if (debouncedQuery) params.q = debouncedQuery;
      const res = await paymentService.listPayments(params);
      setPayments(unwrapPayments(res));
      setPagination(readPagination(res));
      setTotalAmount(unwrapTotalAmount(res));
      setTodayAmount(unwrapTodayAmount(res));
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to load payments."));
      setPayments([]);
      setTotalAmount(0);
      setTodayAmount(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetails = async (row) => {
    setDetail(row);
    setDetailError("");
    setDetailLoading(true);
    try {
      const res = await paymentService.getPayment(row._id);
      const full = unwrapPayment(res);
      if (full) setDetail(full);
    } catch (err) {
      setDetailError(await getApiErrorMessage(err, "Failed to load payment details."));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setDetail(null);
    setDetailError("");
    setDetailLoading(false);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const params = {};
      if (debouncedQuery) params.q = debouncedQuery;
      const response = await paymentService.downloadPaymentsExcel(params);
      await saveExcelBlob(response, "fastrecovery-payments.xlsx");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to download Excel file."));
    } finally {
      setExporting(false);
    }
  };

  const totalCount = pagination.total || 0;

  return (
    <div className="page payments-page">
      <div className="users-actions">
        <div>
          <h2>Payments</h2>
          <p className="muted">All successful payments across agencies.</p>
        </div>
      </div>

      <div className="payment-summary">
        <div className="payment-summary__card">
          <span className="payment-summary__label">Successful payments</span>
          <strong className="payment-summary__value">{loading ? "…" : totalCount.toLocaleString("en-IN")}</strong>
        </div>
        <div className="payment-summary__card">
          <span className="payment-summary__label">Total amount</span>
          <strong className="payment-summary__value">{loading ? "…" : formatAmount(totalAmount)}</strong>
        </div>
        <div className="payment-summary__card payment-summary__card--today">
          <span className="payment-summary__label">Today amount</span>
          <strong className="payment-summary__value">{loading ? "…" : formatAmount(todayAmount)}</strong>
        </div>
      </div>

      <div className="company-search-panel company-search-panel--simple">
        <div className="company-search-bar">
          <FiSearch className="company-search-icon" aria-hidden />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by admin, company, plan, amount, or payment ID..."
            aria-label="Search payments"
          />
        </div>
      </div>

      {error ? <p className="error-text">{error}</p> : null}

      <DataTableToolbar
        onDownloadExcel={handleExportExcel}
        onPrint={printTablePage}
        downloading={exporting}
        downloadLabel="Download Excel (.xlsx)"
      />

      {loading && payments.length === 0 ? (
        <p className="muted">Loading payments…</p>
      ) : (
        <>
          <p className="company-results-count">
            {debouncedQuery
              ? `${totalCount} matching payment${totalCount === 1 ? "" : "s"}`
              : `${totalCount} successful payment${totalCount === 1 ? "" : "s"}`}
          </p>
          <div className="company-table-wrap printable-table-area">
            <h3 className="print-only-title">Successful payments</h3>
            <table className="users-table excel-grid-table">
              <thead>
                <tr>
                  <th>Payment Date</th>
                  <th>Paid by (Admin)</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Type / Plan</th>
                  <th>Amount</th>
                  <th>Payment ID</th>
                  <th>Source</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      {debouncedQuery
                        ? "No payments match your search."
                        : "No successful payment records yet."}
                    </td>
                  </tr>
                ) : (
                  payments.map((row) => (
                    <tr
                      key={row._id}
                      className="payment-row"
                      onClick={() => openDetails(row)}
                    >
                      <td>{formatDateTime(row.paidAt || row.createdAt)}</td>
                      <td>
                        <div className="payment-admin-cell">
                          <strong>{paidByName(row)}</strong>
                          {row.paidBy?.email && row.paidBy?.name ? (
                            <span>{row.paidBy.email}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>{formatRepoRole(row.paidBy?.role)}</td>
                      <td>
                        <div className="payment-admin-cell">
                          <strong>{companyName(row)}</strong>
                          {row.company?.companyCode ? (
                            <span>{row.company.companyCode}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className="payment-plan-chip">{row.planLabel || "—"}</span>
                      </td>
                      <td className="payment-amount-cell">{formatAmount(row.amount)}</td>
                      <td>
                        <code className="payment-id-code">{row.razorpayPaymentId || "—"}</code>
                      </td>
                      <td>
                        <span className="payment-source-badge">
                          {row.paymentSource || "RAZORPAY"}
                        </span>
                      </td>
                      <td className="company-actions-td" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="company-btn company-btn--view"
                          onClick={() => openDetails(row)}
                        >
                          <FiEye aria-hidden />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={pagination.page || page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            loading={loading}
            onPageChange={setPage}
          />
        </>
      )}

      {(detail || detailLoading || detailError) && (
        <PaymentDetailModal
          payment={detail}
          loading={detailLoading}
          error={detailError}
          onClose={closeDetails}
        />
      )}
    </div>
  );
}
